import React from 'react';
import { type FieldError } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import type { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import classNames from 'classnames';
import sortBy from 'lodash/sortBy';
import useOpportunityStages from '@/hooks/swr/useOpportunityStages';
import translate from '@/lib/intl/translate';
import type OpportunityStage from '@/types/api/OpportunityStage';
import type ArrayIfMulti from '@/types/helpers/ArrayIfMulti';
import type SingleOrMultiSelectProps from '@/types/SingleOrMultiSelectProps';

const MENU_PROPS: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  classes: { paper: 'max-h-96' },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

type StageSelectProps<IsMulti extends boolean> = SingleOrMultiSelectProps<
  string,
  IsMulti
> & {
  errors?: FieldError;
};

export default function StageSelect<IsMulti extends boolean = false>({
  className,
  displayEmpty,
  errors,
  multiple,
  onChange,
  required,
  value,
}: StageSelectProps<IsMulti>) {
  const { data } = useOpportunityStages();

  const handleChange = React.useCallback(
    (event: SelectChangeEvent<ArrayIfMulti<string, IsMulti>>) => {
      onChange(event.target.value as ArrayIfMulti<string, IsMulti>);
    },
    [onChange],
  );

  const options = React.useMemo(
    () =>
      sortBy(
        (data?.opportunityStages || []).map(
          (opportunityStage: OpportunityStage) => ({
            value: opportunityStage.opportunityStageId,
            text: opportunityStage.name,
          }),
        ),
        ['order'],
      ),
    [data],
  );

  const shrinkLabel =
    (multiple && !!value?.length) || (!multiple && !!value) || displayEmpty;

  return (
    <FormControl
      className={classNames(className, 'w-full')}
      required={required}
      size="small"
    >
      <InputLabel
        id="opportunity-stage-select-label"
        error={!!errors}
        shrink={shrinkLabel}
      >
        {translate('OPPORTUNITIES_STAGE_LABEL')}
      </InputLabel>
      <Select
        MenuProps={MENU_PROPS}
        displayEmpty={displayEmpty}
        error={!!errors}
        id="opportunity-stage-select"
        label={translate('OPPORTUNITIES_STAGE_LABEL')}
        labelId="opportunity-stage-select-label"
        multiple={multiple}
        notched={shrinkLabel}
        onChange={handleChange}
        renderValue={(selected) => {
          if (multiple) {
            if (!selected?.length && displayEmpty) {
              return translate('SHOW_ALL');
            }
            return options
              .reduce<string[]>((prev, curr) => {
                if (selected.includes(curr.value)) {
                  return [...prev, curr.text];
                }
                return prev;
              }, [])
              .join(', ');
          }
          return options.find((option) => selected === option.value)?.text;
        }}
        value={value}
      >
        {!required && !multiple && (
          <MenuItem value="">{translate('NONE')}</MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            className="flex w-full items-center"
            key={option.value}
            value={option.value}
          >
            {multiple && (
              <Checkbox
                checked={value.includes(option.value)}
                className="py-2 pl-0"
              />
            )}
            {option.text}
          </MenuItem>
        ))}
      </Select>
      {!!errors && <FormHelperText error>{errors.message}</FormHelperText>}
    </FormControl>
  );
}
